import React from 'react'

import { Absolute, BackgroundImage, Box, Card, Flex, Icon, Text, Truncate } from 'stardust'
import * as borderRadius from 'stardust/utils/borderRadius'
import styled from 'styled-components'

import Placeholder from '~/components/DocumentCardPlaceholder'
import DocumentIcon from '~/components/DocumentIcon'
import type { DropdownMenuItemProps } from '~/components/DropdownMenu'
import OverflowMenu from '~/components/OverflowMenu'
import { isPlayground } from '~/config'
import { articleContentState } from '~/utils'

const BoxContainer = styled(Box)`
  position: relative;
`

const Container = styled(Card)`
  box-shadow: ${(props) => props.theme.shadows['1dp']};
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: ${(props) => props.theme.shadows['2dp']};
  }
`

const PlaceholderContainer = styled(Absolute)`
  display: flex;
  justify-content: space-between;
  width: 48px;
`
const MultiIconContainer = styled(Absolute)`
  display: flex;
  height: 30px;
  justify-content: space-between;
  width: 64px;
`
const QuarterCircleLeft = styled(Box)`
  border-radius: 100% 0% 0% 0%;
  height: 30px;
  width: 100%;
`
const MultiIconsQuarterCircleLeft = styled(Box)`
  border-radius: 50% 0% 0% 0%;
  height: 30px;
  width: 100%;
`
const QuarterCircleRight = styled(Box)`
  border-radius: 0% 100% 0% 0%;
  height: 30px;
  width: 100%;
`
const MultiIconsQuarterCircleRight = styled(Box)`
  border-radius: 0% 50% 0% 0%;
  height: 30px;
  width: 100%;
`
const HiddenBox = styled(Box)`
  ${borderRadius.topLeft}
  ${borderRadius.topRight}

  overflow: hidden;
`

interface ContentStatusIndicatorProps {
  isPublished?: boolean
  hasUnpublishedChanges?: boolean
}

const ContentStatusIndicator = ({ isPublished, hasUnpublishedChanges }: ContentStatusIndicatorProps) => {
  const contentState =
    isPublished && hasUnpublishedChanges ? articleContentState({ isPublished, hasUnpublishedChanges }) : null

  const humanizedContentState = (contentState: string) => {
    switch (contentState) {
      case 'DRAFT':
        return 'Draft'
      case 'PUBLISHED':
        return 'Published'
      case 'UNPUBLISHED_CHANGES':
        return 'Unpublished Changes'
    }
  }

  return (
    <Absolute top={24} right={24}>
      {contentState && isPlayground && (
        <Box border={1} borderColor="cosmicShade7" borderRadius={1} p={1} width="auto">
          <Flex alignItems="center">
            <Text.span px={2} fontSize={0} lineHeight={1.4} medium>
              {humanizedContentState(contentState)}
            </Text.span>
          </Flex>
        </Box>
      )}
    </Absolute>
  )
}

interface Props {
  commentCount?: number
  coverImage?: string
  hasUnpublishedChanges?: boolean
  id: number
  isPublished?: boolean
  menuItems?: DropdownMenuItemProps[]
  title: string
  thumbImage?: string
  type: Playground.DocumentType
  onClick?(): void
}

const DocumentCard = ({
  commentCount,
  coverImage,
  hasUnpublishedChanges,
  id,
  isPublished,
  menuItems,
  title,
  type,
  onClick,
}: Props) => {
  const isDraft = type.includes('learning_') && !isPublished
  const textStyle = menuItems ? { maxWidth: '160px' } : { maxWidth: '220px' }

  return (
    <BoxContainer>
      <Container
        borderRadius={0}
        height="280px"
        id={`${type}-${id}`}
        m={3}
        width="220px"
        onClick={onClick}
        data-test="document-card">
        {coverImage ? (
          <HiddenBox brTopLeft={0} brTopRight={0}>
            <BackgroundImage image={coverImage} width={1} height="232px" />
          </HiddenBox>
        ) : (
          <Placeholder draft={isDraft} type={type} />
        )}

        {type.includes('learning_') && (
          <ContentStatusIndicator isPublished={isPublished} hasUnpublishedChanges={hasUnpublishedChanges} />
        )}
        {commentCount ? (
          <MultiIconContainer top="233px" left="26px">
            <MultiIconsQuarterCircleLeft bg="surfacePrimary" />
            <MultiIconsQuarterCircleRight bg="surfacePrimary" />
            <Absolute top={4} left={8}>
              <DocumentIcon type={type} />
            </Absolute>
            <Absolute top={4} left={34}>
              <Icon width={20} height={19} name="comments" mr={1} fill="primary" />
            </Absolute>
          </MultiIconContainer>
        ) : (
          <PlaceholderContainer top="233px" left="26px">
            <QuarterCircleLeft bg="surfacePrimary" />
            <Absolute top={4} left={14}>
              <DocumentIcon type={type} />
            </Absolute>
            <QuarterCircleRight bg="surfacePrimary" />
          </PlaceholderContainer>
        )}

        <Flex alignItems="center" justifyContent="space-between">
          <Text.span fontSize={1} lineHeight={1.4} maxWidth={menuItems ? '170px' : '200px'} px={2} py={3}>
            <Truncate style={textStyle} data-test="document-card-title">
              {title}
            </Truncate>
          </Text.span>

          {menuItems && menuItems.length > 0 && <OverflowMenu items={menuItems} />}
        </Flex>
      </Container>
    </BoxContainer>
  )
}

DocumentCard.displayName = 'DocumentCard'

export default React.memo(DocumentCard)
